import { SignInMutationVariables, SocialAuthMutationVariables } from '@graphql/mutations/__generated__/auth.generated'
import { Divider } from '@mui/material'
import Button from '@mui/material/Button'
import { Form, Formik } from 'formik'
import React, { useMemo } from 'react'
import * as Yup from 'yup'
import FormikErrors from '../../../Formik/FormikErrors'
import SocialAuthButtons from '../../../SocialAuthButtons'
import SignInFormFields from './SignInFormFields'
import { useTranslation } from 'react-i18next'

interface IProps {
  formErrors: { messages: string[]; field?: string }[] | null

  onSubmitSignIn(values: SignInMutationVariables): Promise<void>

  onForgotPassword(e: any): void

  onSubmitSocialSignIn(values: SocialAuthMutationVariables): Promise<void>
}

interface IDialogProps extends IProps {
  variant: 'dialog'

  handleSignUp(): void

  handleCloseModal(): void
}

interface IDefaultProps extends IProps {
  onForgotPassword(e: any): void

  variant?: 'default'
}

const SignInForm: React.FC<IDefaultProps | IDialogProps> = props => {
  const { t, i18n } = useTranslation()
  const { formErrors, onSubmitSocialSignIn, onForgotPassword, onSubmitSignIn } = props
  const schema = useMemo(
    () =>
      Yup.object().shape({
        username: Yup.string()
          .email(t('formErrors.invalidEmail', 'Invalid email'))
          .required(t('formErrors.valueIsRequired', 'This value is required')),
        password: Yup.string()
          .min(2, t('formErrors.tooShort', 'Too Short!'))
          .max(30, t('formErrors.tooLong', 'Too Long!'))
          .required(t('formErrors.valueIsRequired', 'This value is required')),
      }),
    [t, i18n.language]
  )
  return (
    <Formik
      initialValues={{
        username: '',
        password: '',
      }}
      validationSchema={schema}
      onSubmit={async values => {
        await onSubmitSignIn(values)
      }}
    >
      <>
        <FormikErrors errors={formErrors} />
        {props.variant === 'dialog' && (
          <>
            <Form>
              <SignInFormFields onForgotPassword={onForgotPassword} />
              <div>
                <Button type='submit' variant='contained' color='secondary' size='large' fullWidth>
                  <span className='font-semibold tracking-widest'>{t('signIn', 'Sign in')}</span>
                </Button>
                <SocialAuthButtons onSubmitSocialSignIn={onSubmitSocialSignIn} />
              </div>
            </Form>
            <div className='my-20'>
              <Divider />
            </div>
            <div className='flex items-center justify-center'>
              <span className='font-bold'>{t('dontHaveAnAccount', "Don't have an account?")}</span>
              <span>
                <Button variant='text' color='secondary' onClick={props.handleSignUp} disableRipple>
                  <span className='font-semibold text-capitalize'>{t('signUp', 'Sign up')}</span>
                </Button>
              </span>
            </div>
          </>
        )}
        {(!props.variant || props.variant === 'default') && (
          <Form>
            <legend className='legend mt-0 text-base'>
              {t('signIn', 'Sign in')} <span className='legend-label'>{t('required', 'Required')}</span>
            </legend>
            <SignInFormFields onForgotPassword={onForgotPassword} />
            <div>
              <Button type='submit' variant='contained' color='secondary' size='large' fullWidth>
                <span className='font-semibold tracking-widest'>{t('signIn', 'Sign in')}</span>
              </Button>
              <SocialAuthButtons onSubmitSocialSignIn={onSubmitSocialSignIn} />
            </div>
          </Form>
        )}
      </>
    </Formik>
  )
}

export default SignInForm
